import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  GetQuestionsByTagQuery,
  GetQuizByIdQuery,
  GetUserByEmailQuery,
  SelectedOption,
  UserQuestion,
} from '../__generated__/graphql';
import { useState, useEffect, useCallback } from 'react';
import { GET_QUIZ_BY_ID } from '../graphql/getQuizById';
import { UPDATE_QUIZ } from '../graphql/updateQuiz';
import { COMPLETE_QUIZ } from '../graphql/completeQuiz';
import { CREATE_QUIZ } from '../graphql/createQuiz';
import { GET_USER_BY_EMAIL } from '../graphql/getUserByEmail';
import { GET_QUESTIONS_BY_TAG } from '../graphql/getQuestionsByTag';
import { BOOKMARK_QUESTION } from '../graphql/bookMarkQuestion';
import { GET_BOOKMARKED_QUESTIONS_FOR_USER } from '../graphql/getBookmarkedQuestionsForUser';
import { RESET_USER_QUESTIONS_SEEN } from '../graphql/resetUserQuestionsSeen';
import { GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER } from '../graphql/getAllInProgressQuizzesForUser';
import { GET_ALL_COMPLETED_QUIZZES_FOR_USER } from '../graphql/getAllCompletedQuizzesForUser';
import { RESET_USER_CORRECT_QUESTIONS } from '../graphql/resetUserCorrectQuestions';

export const useQuiz = () => {
  const [currIndex, setCurrIndex] = useState<number>(0);
  const [isConfettiExploding, setIsConfettiExploding] = useState<boolean>(false);
  const [isCompleteQuizModalOpen, setIsCompleteQuizModalOpen] = useState<boolean>(false);
  const [isNextRoundModalOpen, setIsNextRoundModalOpen] = useState<boolean>(false);
  
  const navigate = useNavigate();
  const client = useApolloClient();
  const { id } = useParams();
  const { state } = useLocation();

  // Queries
  const { data, loading } = useQuery<GetQuizByIdQuery>(GET_QUIZ_BY_ID, {
    variables: { id },
  });

  const { data: questionsForTagData } = useQuery<GetQuestionsByTagQuery>(
    GET_QUESTIONS_BY_TAG,
    {
      variables: { tag: state?.tag },
    },
  );

  const { data: userData } = useQuery<GetUserByEmailQuery>(GET_USER_BY_EMAIL, {
    variables: { email: state?.email },
  });

  const { data: bookmarkedQuestionsData } = useQuery(
    GET_BOOKMARKED_QUESTIONS_FOR_USER,
  );

  // Mutations
  const [updateQuiz] = useMutation(UPDATE_QUIZ);
  const [completeQuiz] = useMutation(COMPLETE_QUIZ);
  const [createQuiz] = useMutation(CREATE_QUIZ);
  const [resetUserQuestionsSeen] = useMutation(RESET_USER_QUESTIONS_SEEN);
  const [resetUserCorrectQuestions] = useMutation(RESET_USER_CORRECT_QUESTIONS);
  const [bookmarkQuestion] = useMutation(BOOKMARK_QUESTION);

  // Derived state
  const userQuestions = data?.getQuizById.userQuestions;

  // Helper functions
  const isCurrentQuestionBookmarked = useCallback(() => {
    if (!bookmarkedQuestionsData?.getBookmarkedQuestionsForUser || !userQuestions) {
      return false;
    }
    const currentQuestionId = userQuestions[currIndex]?.question.id;
    return bookmarkedQuestionsData.getBookmarkedQuestionsForUser.some(
      (question: { id: string }) => question.id === currentQuestionId,
    );
  }, [bookmarkedQuestionsData, userQuestions, currIndex]);

  const hasUserSeenAllQuestions = useCallback(() => {
    const totalQuestionsForTag = questionsForTagData?.getQuestionsByTag.length;
    const questionsSeen = userData?.getUserByEmail.questionsSeen;
    const questionsSeenForTag = questionsSeen?.find(
      (question) => question.tag === state?.tag,
    );

    if (!totalQuestionsForTag || !questionsSeenForTag) {
      return false;
    }

    return questionsSeenForTag.questionIds.length === totalQuestionsForTag;
  }, [questionsForTagData, userData, state?.tag]);

  const isAllQuestionsCompleted = useCallback(() => {
    if (!userQuestions) {
      return false;
    }
    for (const userQuestion of userQuestions) {
      if (!userQuestion.selectedOption) {
        return false;
      }
    }
    return true;
  }, [userQuestions]);

  const calculateScore = useCallback(() => {
    let score = 0;
    for (const userQuestion of userQuestions ?? []) {
      if (userQuestion.selectedOption?.isAnswer) {
        score++;
      }
    }
    return score;
  }, [userQuestions]);

  // Event handlers
  const handleBookmarkToggle = useCallback(async () => {
    if (!userQuestions) return;

    const currentQuestionId = userQuestions[currIndex]?.question.id;
    if (!currentQuestionId) return;

    try {
      // Use the single bookmarkQuestion mutation for both bookmarking and unbookmarking
      await bookmarkQuestion({
        variables: { questionId: currentQuestionId },
        refetchQueries: [{ query: GET_BOOKMARKED_QUESTIONS_FOR_USER }],
        update: (cache) => {
          // Update bookmarked questions cache
          const existingData = cache.readQuery({ query: GET_BOOKMARKED_QUESTIONS_FOR_USER }) as any;
          if (existingData?.getBookmarkedQuestionsForUser) {
            const isCurrentlyBookmarked = existingData.getBookmarkedQuestionsForUser.some(
              (question: { id: string }) => question.id === currentQuestionId
            );
            
            if (isCurrentlyBookmarked) {
              // Remove from bookmarks
              cache.writeQuery({
                query: GET_BOOKMARKED_QUESTIONS_FOR_USER,
                data: {
                  getBookmarkedQuestionsForUser: existingData.getBookmarkedQuestionsForUser.filter(
                    (question: { id: string }) => question.id !== currentQuestionId
                  ),
                },
              });
            } else {
              // Add to bookmarks
              const currentQuestion = questionsForTagData?.getQuestionsByTag.find(
                (q) => q.id === currentQuestionId
              );
              if (currentQuestion) {
                cache.writeQuery({
                  query: GET_BOOKMARKED_QUESTIONS_FOR_USER,
                  data: {
                    getBookmarkedQuestionsForUser: [
                      ...existingData.getBookmarkedQuestionsForUser,
                      currentQuestion,
                    ],
                  },
                });
              }
            }
          }
        },
      });
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }, [userQuestions, currIndex, bookmarkQuestion, questionsForTagData]);

  const handleResetUserQuestionsSeen = useCallback(async () => {
    try {
      await resetUserQuestionsSeen({
        variables: { tag: state?.tag },
        update: (cache) => {
          // Update user data cache to reset questions seen for the specific tag
          const existingData = cache.readQuery({
            query: GET_USER_BY_EMAIL,
            variables: { email: state?.email },
          }) as any;
          if (existingData?.getUserByEmail) {
            const updatedQuestionsSeen = existingData.getUserByEmail.questionsSeen?.map(
              (entry: any) => entry.tag === state?.tag ? { ...entry, questionIds: [] } : entry
            ) || [];
            
            cache.writeQuery({
              query: GET_USER_BY_EMAIL,
              variables: { email: state?.email },
              data: {
                getUserByEmail: {
                  ...existingData.getUserByEmail,
                  questionsSeen: updatedQuestionsSeen,
                },
              },
            });
          }
        },
      });
    } catch (error) {
      console.error('Error resetting user questions seen: ', error);
    }
  }, [resetUserQuestionsSeen, state?.tag, state?.email]);

  const handleResetUserCorrectQuestions = useCallback(async () => {
    try {
      await resetUserCorrectQuestions({
        variables: { tag: state?.tag },
        update: (cache) => {
          // Update user data cache to reset correct questions for the specific tag
          const existingData = cache.readQuery({
            query: GET_USER_BY_EMAIL,
            variables: { email: state?.email },
          }) as any;
          if (existingData?.getUserByEmail) {
            const updatedCorrectQuestions = existingData.getUserByEmail.correctQuestions?.map(
              (entry: any) => entry.tag === state?.tag ? { ...entry, questionIds: [] } : entry
            ) || [];
            
            cache.writeQuery({
              query: GET_USER_BY_EMAIL,
              variables: { email: state?.email },
              data: {
                getUserByEmail: {
                  ...existingData.getUserByEmail,
                  correctQuestions: updatedCorrectQuestions,
                },
              },
            });
          }
        },
      });
    } catch (error) {
      console.error('Error resetting user correct questions: ', error);
    }
  }, [resetUserCorrectQuestions, state?.tag, state?.email]);

  const handleUpdateQuiz = useCallback(async (
    questionId: string,
    selectedOption: SelectedOption,
  ) => {
    if (!userQuestions || !id) return;

    const copyQuestions = userQuestions.map((userQuestion) =>
      userQuestion.question.id === questionId
        ? { ...userQuestion, selectedOption }
        : userQuestion,
    );

    const updateQuizInput = {
      id,
      userQuestions: copyQuestions.map((q) => ({
        questionId: q.questionId,
        selectedOption: {
          desc: q.selectedOption.desc,
          isAnswer: q.selectedOption.isAnswer,
        },
      })),
    };

    try {
      await updateQuiz({
        variables: { updateQuizInput },
        update: (cache, { data: updateData }) => {
          if (updateData?.updateQuiz) {
            // Update the quiz cache with the new data
            cache.writeQuery({
              query: GET_QUIZ_BY_ID,
              variables: { id },
              data: {
                getQuizById: {
                  ...data?.getQuizById,
                  userQuestions: copyQuestions,
                  ...updateData.updateQuiz,
                },
              },
            });

            // Update the in-progress quizzes cache
            const inProgressData = cache.readQuery({ query: GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER }) as any;
            if (inProgressData?.getAllInProgressQuizzesForUser) {
              const updatedInProgress = inProgressData.getAllInProgressQuizzesForUser.map(
                (quiz: any) => quiz.id === id ? { ...quiz, userQuestions: copyQuestions } : quiz
              );
              cache.writeQuery({
                query: GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER,
                data: { getAllInProgressQuizzesForUser: updatedInProgress },
              });
            }

            // Update the completed quizzes cache if the quiz is completed
            if (updateData.updateQuiz.isCompleted) {
              const completedData = cache.readQuery({ query: GET_ALL_COMPLETED_QUIZZES_FOR_USER }) as any;
              if (completedData?.getAllCompletedQuizzesForUser) {
                const updatedCompleted = completedData.getAllCompletedQuizzesForUser.map(
                  (quiz: any) => quiz.id === id ? { ...quiz, userQuestions: copyQuestions, isCompleted: true } : quiz
                );
                cache.writeQuery({
                  query: GET_ALL_COMPLETED_QUIZZES_FOR_USER,
                  data: { getAllCompletedQuizzesForUser: updatedCompleted },
                });
              }
            }
          }
        },
      });
    } catch (error) {
      console.error('Error updating quiz: ', error);
    }
  }, [userQuestions, id, updateQuiz, data?.getQuizById]);

  const handleCompleteQuiz = useCallback(async () => {
    if (!id) return;

    try {
      await completeQuiz({
        variables: { id },
        update: (cache, { data: completeData }) => {
          if (completeData?.completeQuiz) {
            // Update the quiz cache
            cache.writeQuery({
              query: GET_QUIZ_BY_ID,
              variables: { id },
              data: {
                getQuizById: {
                  ...data?.getQuizById,
                  isCompleted: true,
                  ...completeData.completeQuiz,
                },
              },
            });

            // Move quiz from in-progress to completed in cache
            const inProgressData = cache.readQuery({ query: GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER }) as any;
            const completedData = cache.readQuery({ query: GET_ALL_COMPLETED_QUIZZES_FOR_USER }) as any;
            
            if (inProgressData?.getAllInProgressQuizzesForUser) {
              const updatedInProgress = inProgressData.getAllInProgressQuizzesForUser.filter(
                (quiz: any) => quiz.id !== id
              );
              cache.writeQuery({
                query: GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER,
                data: { getAllInProgressQuizzesForUser: updatedInProgress },
              });
            }

            if (completedData?.getAllCompletedQuizzesForUser) {
              const quizToMove = data?.getQuizById;
              if (quizToMove) {
                const updatedCompleted = [
                  ...completedData.getAllCompletedQuizzesForUser,
                  { ...quizToMove, isCompleted: true },
                ];
                cache.writeQuery({
                  query: GET_ALL_COMPLETED_QUIZZES_FOR_USER,
                  data: { getAllCompletedQuizzesForUser: updatedCompleted },
                });
              }
            }
          }
        },
      });
      setIsNextRoundModalOpen(true);
    } catch (error) {
      console.error('Error completing quiz: ', error);
    }
  }, [id, completeQuiz, data?.getQuizById]);

  const handleResetProgress = useCallback(() => {
    handleResetUserQuestionsSeen();
    handleResetUserCorrectQuestions();
    setIsConfettiExploding(false);
    navigate('/dashboard');
  }, [handleResetUserQuestionsSeen, handleResetUserCorrectQuestions, navigate]);

  const handleNavigateToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleNavigateToHistory = useCallback(() => {
    navigate('/completed-rounds');
  }, [navigate]);

  const handleCloseCompleteQuizModal = useCallback(() => {
    setIsCompleteQuizModalOpen(false);
  }, []);

  const handleCloseNextRoundModal = useCallback(() => {
    setIsNextRoundModalOpen(false);
  }, []);

  const handleCreateNewQuiz = useCallback(async () => {
    if (!state?.tag || !userData?.getUserByEmail.id) return;

    try {
      const { data: newQuizData } = await createQuiz({
        variables: {
          tag: state.tag,
          userId: userData.getUserByEmail.id,
        },
        update: (cache, { data: createData }) => {
          if (createData?.createQuiz) {
            // Add new quiz to in-progress quizzes cache
            const inProgressData = cache.readQuery({ query: GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER }) as any;
            if (inProgressData?.getAllInProgressQuizzesForUser) {
              cache.writeQuery({
                query: GET_ALL_IN_PROGRESS_QUIZZES_FOR_USER,
                data: {
                  getAllInProgressQuizzesForUser: [
                    ...inProgressData.getAllInProgressQuizzesForUser,
                    createData.createQuiz,
                  ],
                },
              });
            }
          }
        },
      });

      if (newQuizData?.createQuiz) {
        // Navigate to the new quiz
        navigate(`/quiz/${newQuizData.createQuiz.id}`, { 
          state: { 
            tag: state.tag,
            email: state.email 
          } 
        });
        
        // Reload the browser to reset all states
        window.location.reload();
      }
    } catch (error) {
      console.error('Error creating new quiz: ', error);
    }
  }, [state?.tag, state?.email, userData?.getUserByEmail.id, createQuiz, navigate]);

  const handleQuestionSelection = useCallback((questionId: string, selectedOption: SelectedOption) => {
    if (!data?.getQuizById.isCompleted) {
      handleUpdateQuiz(questionId, selectedOption);
    }
  }, [data?.getQuizById.isCompleted, handleUpdateQuiz]);

  const handlePreviousQuestion = useCallback(() => {
    setCurrIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleNextQuestion = useCallback(() => {
    if (userQuestions) {
      // If we're on the last question and all questions are answered, complete the quiz
      if (currIndex === userQuestions.length - 1 && isAllQuestionsCompleted()) {
        handleCompleteQuiz();
        return;
      }
      
      // Otherwise, move to next question
      setCurrIndex((prev) =>
        prev < userQuestions.length - 1 ? prev + 1 : prev,
      );
    }
  }, [userQuestions, currIndex, isAllQuestionsCompleted, handleCompleteQuiz]);

  const handleQuestionClick = useCallback((index: number) => {
    if (!userQuestions) return;
    
    const isAnswered = !!userQuestions[index].selectedOption.desc;
    const currentQuestionAnswered = !!userQuestions[currIndex]?.selectedOption.desc;
    const isClickable = isAnswered || index <= currIndex || currentQuestionAnswered;
    
    if (isClickable) {
      setCurrIndex(index);
    }
  }, [userQuestions, currIndex]);

  const isCurrentQuestionAnswered = useCallback(() => {
    if (!userQuestions || !userQuestions[currIndex]) return false;
    return !!userQuestions[currIndex].selectedOption.desc;
  }, [userQuestions, currIndex]);

  // Effects
  useEffect(() => {
    if (hasUserSeenAllQuestions()) {
      setIsConfettiExploding(true);
    }
  }, [hasUserSeenAllQuestions]);

  return {
    // State
    currIndex,
    isConfettiExploding,
    isCompleteQuizModalOpen,
    isNextRoundModalOpen,
    loading,
    
    // Data
    userQuestions,
    quizData: data?.getQuizById,
    questionsForTagData,
    userData,
    bookmarkedQuestionsData,
    
    // Computed values
    isCurrentQuestionBookmarked: isCurrentQuestionBookmarked(),
    hasUserSeenAllQuestions: hasUserSeenAllQuestions(),
    isAllQuestionsCompleted: isAllQuestionsCompleted(),
    isCurrentQuestionAnswered: isCurrentQuestionAnswered(),
    score: calculateScore(),
    totalQuestions: userQuestions?.length || 0,
    isCompleted: data?.getQuizById.isCompleted || false,
    
    // Event handlers
    handleBookmarkToggle,
    handleResetProgress,
    handleNavigateToDashboard,
    handleNavigateToHistory,
    handleCloseCompleteQuizModal,
    handleCloseNextRoundModal,
    handleCompleteQuiz,
    handleCreateNewQuiz,
    handleQuestionSelection,
    handlePreviousQuestion,
    handleNextQuestion,
    handleQuestionClick,
  };
};
